import { CurrencyPipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  Clinic,
  Consultation,
  ConversionConfiguration,
  Criteria,
  NutritionData,
  NutritionDataProduct,
  PetInfo,
  PetOwner,
  ProductRecommendation,
  Recommendation,
  SmartRecoApiBody,
  UtmParamsContext,
  Vet,
  Weight,
} from '@app/core/models';
import {
  RecommendationBodyEmail,
  SignalRecommendationBodyEmail,
  SignalRecommendationTechnicalInformation,
} from '@app/core/models/body-email';
import { IBuildPOLetterData } from '@app/core/models/build-po-letter-data';
import { SvgSize, WeightChart } from '@app/core/models/chart';
import { ConsultationWeight } from '@app/core/models/consultation-weight.js';
import { TechnicalParamsType } from '@app/core/models/market';
import { PathologyCategory } from '@app/core/models/pathology';
import { TemplateMailInterface, TemplateProduct } from '@app/pages/templates/templates-interface';
import { lifestageTypeTranslationCodes } from '@app/shared/data';
import { PATHOLOGIES } from '@app/shared/data/pathologies';
import { FractionCupPipe } from '@app/shared/pipes';
import {
  Constants,
  EmailPopinType,
  MeasureHelper,
  ProductType,
  RenalDetectStatusEnum,
  roundSmartReco,
  SpeciesCode,
  Tool,
} from '@app/shared/utils';
import {
  getTranslationKeyStartWith,
  translateKey,
  translateKeyFromCustomLanguage,
  translationExist,
} from '@app/shared/utils/static-helpers/translate';
import { VetPreferences } from '@app/shared/utils/vet-preferences';
import { environment } from '@env/environment';
import { domain } from '@env/environment.develop';
import { QRCodeToDataURLOptions, toDataURL } from 'qrcode';
import { GenderCode, MeasureConversionEnum, NutritionalChoice, PackType, POLetterFrom, PoLetterType } from '../enums';
import { CriteriaType } from '../enums/criteria-type.enum';
import { Helper, resolvePath } from './helper';
import { getTotalPricePerDay } from './rationing-helper';
import { getTranslateCivility } from '@app/shared/utils/static-helpers/vet-helper';

export function buildRecommendationBodyEmail({
  data: { vet, clinic, petInfo, productRecommendation, currentConsultation, tool, emailPopinType, owner, chronoVetLink },
}: {
  data: {
    vet: Vet;
    clinic: Clinic;
    petInfo: PetInfo;
    productRecommendation: Recommendation;
    currentConsultation: Consultation;
    tool: Tool;
    emailPopinType: EmailPopinType;
    owner: PetOwner;
    chronoVetLink?: string;
  };
}): RecommendationBodyEmail {
  return {
    tool,
    emailPopinType,
    chronoVetLink,
    user: {
      name: `${vet.user?.firstName}${vet.user?.firstName && vet.user?.lastName ? ' ' : ''}${vet.user?.lastName}`,
      clinic_name: clinic?.legalName || '',
      clinic_logo: clinic?.logo.path || null,
      address: clinic?.companyAddress?.address1 || '',
      zip_code: `${clinic?.companyAddress?.zip || ''}`,
      city_name: clinic?.companyAddress?.city || '',
    },
    pet: {
      name: petInfo.name,
    },
    owner: {
      name: owner ? `${owner.givenName} ${owner.familyName}` : null,
    },
    eventData: {
      consultationId: currentConsultation?.id,
      recommendationId: currentConsultation?.visit?.recommendationId,
      clinicId: clinic.id,
    },
    product1: formatProductsForEmail(productRecommendation?.nutritionData)[0],
    product2: formatProductsForEmail(productRecommendation?.nutritionData)[1],
    newPoLetter: true,
  };
}

function formatProductsForEmail(nutritionData: NutritionData[]) {
  return nutritionData.map((item) => ({
    name: item.product?.productDetail?.details?.name,
    url: item.product?.productDetail?.details?.publicUrl,
  }));
}
export function buildSignalRecommendationBodyEmail({
  clinic,
  vet,
  srApiBody,
  petInfo,
  message,
  tool_flow_id,
  technicalInformation,
  product,
}: {
  clinic: Clinic;
  vet: Vet;
  srApiBody: SmartRecoApiBody;
  petInfo: PetInfo;
  message: string;
  tool_flow_id: string;
  technicalInformation: SignalRecommendationTechnicalInformation;
  product?: ProductRecommendation;
}): SignalRecommendationBodyEmail {
  return {
    clinic,
    vet,
    input: srApiBody,
    product: product?.product,
    coverage: product?.coverage,
    petInfo: {
      bcs: petInfo.bcs || 0,
      birthdate: petInfo.birthdate || null,
      breed: petInfo.breed || '',
      gender: petInfo.gender || null,
      IBW: petInfo.IBW || 0,
      lifestage: petInfo.lifestage || null,
      name: petInfo.name || '',
      neutered: petInfo.neutered || false,
      speciesCode: petInfo.speciesCode || null,
      weight: petInfo.weight || 0,
      reproductionStatus: petInfo.reproductionStatus || null,
      bigMeasurementUnit: VetPreferences.currentBigMeasurementUnit,
      pathologies: petInfo.pathologies
        ? petInfo.pathologies.map((value: string) => {
            return {
              label: translateKey(`pathology-${value}`),
              value,
            };
          })
        : [],
      sensitivities: petInfo.sensitivities
        ? petInfo.sensitivities.map((value: string) => {
            return {
              label: translateKey(`sensitivity-${value}`),
              value,
            };
          })
        : [],
    },
    message,
    env: environment.envName,
    api_session_id: tool_flow_id,
    technicalInformation,
  };
}

export const generateQrCode = (url: string): Promise<string> => {
  const qrCodeOptions: QRCodeToDataURLOptions = {
    errorCorrectionLevel: 'M',
    width: 58,
    type: 'image/jpeg',
    color: {
      dark: '#000000',
      light: '#FFFFFF',
    },
  };

  return toDataURL(url, qrCodeOptions);
};

function resolveTechnicalParam(
  itemConfig: { path: string; type: TechnicalParamsType; separator?: string },
  products: NutritionDataProduct[],
  clinic: Clinic,
  vet: Vet
): string | number | boolean {
  switch (itemConfig.type) {
    case TechnicalParamsType.CLINIC:
      return resolvePath(clinic, itemConfig.path);
    case TechnicalParamsType.VET:
      return resolvePath(vet, itemConfig.path);
    case TechnicalParamsType.FIRST_PRODUCT:
      return products[0] ? resolvePath(products[0], itemConfig.path) : '';
    case TechnicalParamsType.SECOND_PRODUCT:
      return products[1] ? resolvePath(products[1], itemConfig.path) : '';
    case TechnicalParamsType.PRODUCTS:
      return (products || []).map((product) => resolvePath(product, itemConfig.path)).join(itemConfig.separator);
    default:
      break;
  }
}

export function getPurchaseLink(
  withPurchaseLink: boolean,
  conversionConfiguration: ConversionConfiguration,
  products: NutritionDataProduct[],
  clinic: Clinic,
  vet: Vet,
  isBodyEmail: boolean,
  utmParamsContextType: UtmParamsContext
): string | null {
  if (!withPurchaseLink || !conversionConfiguration) return null;

  let params = new HttpParams();
  const technicalParamsConfig = conversionConfiguration.linkTechnicalParams || [];
  const utmParamsConfig = (isBodyEmail && conversionConfiguration.linkUtmParamsEmail) || conversionConfiguration.linkUtmParamsPrint || [];

  technicalParamsConfig.forEach((item) => {
    params = params.set(item.key, resolveTechnicalParam(item, products, clinic, vet));
  });

  utmParamsConfig
    .filter((item) => item.context == utmParamsContextType || item.context == UtmParamsContext.ALL)
    .forEach((item) => {
      const paramValue = item.values
        .map((val) => ('type' in val ? resolveTechnicalParam(val, products, clinic, vet)?.toString()?.toLowerCase() : val.text))
        .join('');
      params = params.set(item.key, paramValue);
    });

  return `${conversionConfiguration.linkBaseUrl}?${params.toString()}`;
}

export function buildPOLetterData({
  datePipe,
  currencyPipe,
  fractionCupPipe,
  data: {
    vet,
    clinic,
    petInfo,
    productRecommendation,
    currentConsultation,
    idealBodyWeight,
    withPurchaseLink,
    conversionConfiguration,
    qrCode,
    isRetailPriceActivated,
    mufDryProductId,
    chartImage,
    currency,
    poLetterTranslations,
    weightChart,
    recommendationsCriterias,
    hideDietBlockInPoLetter,
  },
}: IBuildPOLetterData): TemplateMailInterface {
  const formattedWeight = MeasureHelper.convertWeight(
    currentConsultation?.visit?.weight?.measure || petInfo.weight,
    currentConsultation?.visit?.weight?.measureUnit || VetPreferences.currentBigMeasurementUnit,
    VetPreferences.currentBigMeasurementUnit,
    VetPreferences.currentSystemPreferenceCode
  );
  const formattedCurrentWeight = MeasureHelper.convertWeight(
    currentConsultation?.visit?.weight?.measure || petInfo.weight,
    currentConsultation?.visit?.weight?.measureUnit || VetPreferences.currentBigMeasurementUnit,
    VetPreferences.currentBigMeasurementUnit,
    VetPreferences.currentSystemPreferenceCode
  );

  const formattedIdealWeight = MeasureHelper.convertWeight(
    idealBodyWeight.measure,
    idealBodyWeight.measureUnit,
    VetPreferences.currentBigMeasurementUnit,
    VetPreferences.currentSystemPreferenceCode
  );
  const formattedNextVisitWeight = MeasureHelper.convertWeight(
    currentConsultation.nextVisit?.expected?.weight.measure,
    currentConsultation.nextVisit?.expected?.weight.measureUnit,
    VetPreferences.currentBigMeasurementUnit,
    VetPreferences.currentSystemPreferenceCode
  );
  const lifestageTranslation = translateKeyFromCustomLanguage(
    poLetterTranslations.data,
    lifestageTypeTranslationCodes.find((item) => petInfo?.lifestage === item.code).value
  );
  const formattedWeightChartDate: WeightChart[] = weightChart?.map((weight) => {
    return { ...weight, weightDate: datePipe.transform(weight?.weightDate, 'shortDate') };
  });
  const pathologies =
    petInfo?.pathologies?.map((patho) => translateKeyFromCustomLanguage(poLetterTranslations.data, `pathology-${patho}`)) || null;
  const sensitivities =
    petInfo?.sensitivities?.map((sensitivity) => translateKeyFromCustomLanguage(poLetterTranslations.data, `sensitivity-${sensitivity}`)) ||
    null;
  const getTotalPricePerDayValue = getTotalPricePerDay([
    productRecommendation?.nutritionData[0]?.basicRation,
    productRecommendation?.nutritionData[1]?.basicRation,
  ]);
  const { tipsAdvice, tipsDiet } = getTips(recommendationsCriterias, poLetterTranslations?.data, petInfo, hideDietBlockInPoLetter);

  return {
    srSecondPage: !!tipsAdvice.length || !!tipsDiet.length,
    envUrl: domain,
    mixFeeding: productRecommendation.nutritionData.length > 1,
    standaloneType: environment.standaloneType,
    clinic: {
      name: clinic?.legalName,
      address: clinic?.companyAddress?.address1,
      postalCode: clinic?.companyAddress?.zip,
      addressCity: clinic?.companyAddress?.city,
      logoLink: clinic?.logo?.path,
    },
    vet: {
      name: `${vet?.user?.firstName} ${vet?.user?.lastName}`,
      title: getTranslateCivility(vet?.user?.title),
    },
    pet: {
      name: ellipsis(petInfo?.name, 11, 9),
      breed: petInfo?.breedObject?.localName,
      neutred: petInfo?.neutered,
      species: petInfo?.speciesCode,
      isSpeciesCat: petInfo?.speciesCode === SpeciesCode.Cat,
      isSpeciesDog: petInfo?.speciesCode === SpeciesCode.Dog,
      isGenderMale: petInfo?.gender === GenderCode.Male,
      isGenderFemale: petInfo?.gender === GenderCode.Female,
      formattedAge: Helper.getPetAgeText(petInfo?.birthdate, {}),
      lifestage: lifestageTranslation.length ? lifestageTranslation : null,
      BCSValue: petInfo?.bcs,
      BCSStatus: Helper.formatBCSText(petInfo?.bcs, poLetterTranslations?.data),
      formattedWeight: MeasureHelper.weightToString(formattedWeight, 2),
      currentWeight: { measure: +formattedCurrentWeight?.measure.toFixed(2), unit: formattedCurrentWeight.measureUnit },
      formattedCurrentWeight: MeasureHelper.weightToString(formattedCurrentWeight, 2),
      formattedIdealWeight: MeasureHelper.weightToString(formattedIdealWeight, 2),
      formattedNextVisitWeight: MeasureHelper.weightToString(formattedNextVisitWeight, 2),
      formattedWeightDifference: formatWeightDifference(
        formattedNextVisitWeight.measure,
        formattedIdealWeight?.measure,
        formattedNextVisitWeight?.measureUnit
      ),
      formattedWeightNewVisitDifference: formatWeightDifference(
        formattedCurrentWeight.measure,
        formattedNextVisitWeight.measure,
        formattedCurrentWeight.measureUnit
      ),
      tipsAdvice,
      tipsDiet,
    },
    consultation: {
      visitDate:
        datePipe.transform(currentConsultation?.visit?.visitDateTime, 'shortDate') ||
        datePipe.transform(currentConsultation.date, 'shortDate'),
      nextVisitDate: datePipe.transform(currentConsultation.nextVisit?.expected?.visitDateTime, 'shortDate'),
      renalDetectStatus: currentConsultation?.prediction?.status,
      observations: currentConsultation.comments,
      sensitivities,
      pathologies,
      petActivityPerDayEnabled: getPetActivityPerDayEnabled(petInfo),
      pathologiesOrSensitivities: !!(sensitivities?.length || pathologies?.length),
      chartImage,
      idealVisitDate: datePipe.transform(currentConsultation?.targetVisit?.expected?.weight?.weightDate, 'shortDate'),
      isRenalNotAtRisk: currentConsultation?.prediction?.status === RenalDetectStatusEnum.NotAtRisk,
      isRenalAtRisk: currentConsultation?.prediction?.status === RenalDetectStatusEnum.AtRisk,
      isRenalCKD: currentConsultation?.prediction?.status === RenalDetectStatusEnum.CKD,
      weightChart: formattedWeightChartDate,
    },
    purchaseLinkButton: getPurchaseLink(
      withPurchaseLink,
      conversionConfiguration,
      productRecommendation?.nutritionData.map((item) => item.product),
      clinic,
      vet,
      false,
      UtmParamsContext.BUTTON
    ),
    purchaseQrCode: qrCode,
    showPrice: showPrice(isRetailPriceActivated, productRecommendation.nutritionData, !!conversionConfiguration),
    splitPurchaseProductsDisplay: !!mufDryProductId,
    productsTotalPricePerDay: productsTotalPricePerDay(getTotalPricePerDayValue, currency, currencyPipe, poLetterTranslations.data),
    totalPricePerDay: currencyPipe.transform(getTotalPricePerDayValue, currency),
    products: formatNutritionDataPOLetter(
      productRecommendation.nutritionData,
      currency,
      currencyPipe,
      fractionCupPipe,
      poLetterTranslations.data
    ),
    translations: {
      neutredStatus: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_neutered'),
      notNeuteredStatus: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_not-neutered'),
      titleWeightAndBcs: translateKeyFromCustomLanguage(poLetterTranslations.data, 'title_weight-and-bcs'),
      bcs_legend_1_3: translateKeyFromCustomLanguage(poLetterTranslations.data, 'bcs_cat_dog_1_2_3_title'),
      bcs_legend_4_5: translateKeyFromCustomLanguage(poLetterTranslations.data, 'bcs_cat_dog_4_5_title'),
      bcs_legend_6_7: translateKeyFromCustomLanguage(poLetterTranslations.data, 'bcs_cat_dog_7_title'),
      bcs_legend_8_9: translateKeyFromCustomLanguage(poLetterTranslations.data, 'bcs_cat_dog_8_9_title'),
      ageLabel: translateKeyFromCustomLanguage(poLetterTranslations.data, 'label_age'),
      lifestageLabel: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetLifestage'),
      reproductionStatusLabel: translateKeyFromCustomLanguage(poLetterTranslations.data, 'form-attribute_reproduction-status'),

      nextVisit: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossNextVisit'),
      consultationSummaryTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationSummaryTitle'),
      consultationSummaryRenalDetectTitle: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_consultationSummaryTitleRenalDetect'
      ),
      consultationPetAge: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetAge'),
      consultationPetLifeStage: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetLifestage'),
      consultationPetBCS: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_PetBCS'),
      consultationPetBCSValueSuffix: '/9',
      consultationPetWeight: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetWeight'),
      consultationNotesTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationNotesTitle'),
      consultationActivityPlanTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationActivityPlanTitle'),
      consultationActivityPlanToy: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_consultationActivityRenalDetectToy2'
      ),
      consultationActivityPlanPlay: consultationActivityPlanPlay(getPetActivityPerDayEnabled(petInfo), poLetterTranslations.data),
      consultationActivityPlanWalk: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationActivityPlanWalk'),
      consultationActivityPlanToyValue: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_consultationActivityRenalDetectToy1'
      ),
      consultationActivityPlanPlayValue: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_consultationActivityPlanPlayTime'
      ),
      consultationActivityPlanWalkValue: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_consultationActivityPlanWalkDistance'
      ),
      renaldetectConsultationRecoTitle: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_renaldetectConsultationRecoTitle'
      ),
      renaldetectConsultationDescription: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        `po-letter_renaldetectStatus${formatRenalDetectStatus(currentConsultation?.prediction?.status)}Reco1`
      ),
      consultationPathologies: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPathologies'),
      consultationPredispositions: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPredispositions'),
      consultationOtherReminders: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_mailOtherReminders'),

      renalDetectStatusTitle: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        `po-letter_renaldetectStatus${formatRenalDetectStatus(currentConsultation?.prediction?.status)}Title`
      ),
      renalDetectStatusDescription: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        `po-letter_renaldetectStatus${formatRenalDetectStatus(currentConsultation?.prediction?.status)}Description`
      ),

      consultationPetWeightCurve: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetWeightCurve'),
      consultationPetWeightCurveVisits: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_consultationPetWeightCurveVisits'
      ),
      consultationPetWeightIdeal: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetWeightIdeal'),
      consultationPetWeightTarget: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossTarget'),
      consultationPetWeightLegend: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_consultationPetWeightLegend'),
      consultationPetWeightCurrent: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossPatientWeight'),
      weightLossTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossTitle'),
      weightLossIdealWeight: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossIBW'),
      weightLossNextWeight: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossNextVisitGoal'),
      weightLossCurrentWeight: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossCurrentWeight'),
      weightLossText: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossText'),
      weightLossLegend:
        petInfo?.speciesCode === SpeciesCode.Cat
          ? translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossRecoCat')
          : translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_weightlossRecoDog'),

      nutriRecoTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_nutriRecoTitle'),
      nutriRecoProductCostPerDay: translateKeyFromCustomLanguage(poLetterTranslations.data, 'rc-table_cost-title'),
      nutriRecoCostPerDay: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_cost-per-day'),
      nutriRecoProductRation: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_nutriRecoProductRation'),
      nutriRecoProductDailyFeedind: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_daily-feeding'),
      nutriRecoProductName: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_nutriRecoProductName'),
      nutriRecoProductPack: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_nutriRecoPacksize'),
      nutriRecoProductPrice: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_nutriRecoPrice'),
      nutriRecoProductCalories: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_nutriRecoCalories'),
      nutriRecoBuyIvo: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_buy-products-ivo'),
      nutriRecoPersonalisedMuf: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_personalised-mulitfunction'),
      nutriRecoAlternativePackaging: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_alternative-packaging'),
      nutriRecoCurrentWeight: translateKeyFromCustomLanguage(poLetterTranslations.data, 'form-attribute_current-weight'),

      feedingAdviceTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdviceTitle'),
      feedingStep1Title: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdvice1Subtitle'),
      feedingStep1Description: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdvice1Text'),
      feedingStep2Title: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdvice2Subtitle'),
      feedingStep2Description: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdvice2Text'),
      feedingStep3Title: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdvice3Subtitle'),
      feedingStep3Description: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingAdvice3Text'),
      feedingTransitionTitleCat: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionTitleCat'),
      feedingTransitionTitleDog: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionTitleDog'),
      feedingTransitionStep1: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionStep1'),
      feedingTransitionStep2: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionStep2'),
      feedingTransitionStep3: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionStep3'),
      feedingTransitionStep4: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionStep4'),
      feedingTransitionLegend1: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionLegend1'),
      feedingTransitionLegend2: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feedingTransitionLegend2'),

      feedingTransitionDiet: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_feeding-advices_transition-diet', {
        variableKey: 'species',
        variableValue: translateKeyFromCustomLanguage(
          poLetterTranslations.data,
          petInfo?.speciesCode === SpeciesCode.Cat ? 'specie-cat' : 'specie-dog'
        ).toLowerCase(),
      }),

      dailyRationAllocationInformation: translateKeyFromCustomLanguage(
        poLetterTranslations.data,
        'po-letter_new_sentence_daily_ration_allocation'
      ),

      /*********
       * Pruchase Link
       **********/

      nutriRecoProductBuyOnlineLabel: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_integrationBuy'),
      mufPurchaseDisclaimer: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_integrationBuyClinicMultifunctionText'),
      purchaseLinkSectionTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_mailChronovet'),
      purchaseTypeTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, conversionConfiguration?.poLetterTitle || ''),
      purchaseTypeName: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_buy-products-ivo', {
        variableKey: 'ivo',
        variableValue: conversionConfiguration?.poLetterName || '',
      }),
      purchaseTypeClinic: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_integrationBuyClinicMultifunctionTitle'),
      purchaseLinkDescription: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_inetgrationBuySentence'),
      purchaseLinkLegend: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_inetgrationBuySentence2'),
      purchaseLinkSecondLegend: translationExist('po-letter_conversion_online_sku_information')
        ? translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_conversion_online_sku_information')
        : '',
      purchaseLinkBuyExplanation1: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_integrationBuyExplanation1'),
      purchaseLinkBuyExplanation2: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_integrationBuyExplanation2'),
      purchaseLinkBuyExplanation3: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_integrationBuyExplanation3'),
      purchaseLinkBuySentence: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_inetgrationBuySentence'),
      purchaseOrder: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_order'),

      /*********
       * Tips at home
       **********/

      practicalAdviceTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'title_practical-advices'),
      practicalAdviceSubtitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_subtitle_pet-at-home'),

      /*********
       * Diet support
       **********/

      dietSupportTitle: translateKeyFromCustomLanguage(poLetterTranslations.data, 'po-letter_diet-support_title'),

      /*********
       * Miscellaneous
       **********/

      day: translateKeyFromCustomLanguage(poLetterTranslations.data, 'day'),
      days: translateKeyFromCustomLanguage(poLetterTranslations.data, 'days'),
      and: translateKeyFromCustomLanguage(poLetterTranslations.data, 'and'),
    },
    currentDate: datePipe.transform(new Date(), 'shortDate'),
  };
}

const getTips = (
  recommendationsCriterias: Criteria[],
  poLetterTranslationsData: Record<string, string>,
  petInfo: PetInfo,
  hideDietBlockInPoLetter: boolean
) => {
  const pathologicalCriteriasFiltredByPmean = filteredCriteriasByPmean(recommendationsCriterias, 90);
  const tipsAdvice = getTipsOfCriterias(
    poLetterTranslationsData,
    getPathologicalCriterias(recommendationsCriterias),
    petInfo?.speciesCode,
    'tipsathome-',
    2
  );
  const tipsDiet = hideDietBlockInPoLetter
    ? []
    : getTipsOfCriterias(poLetterTranslationsData, pathologicalCriteriasFiltredByPmean, petInfo?.speciesCode, 'dietsupport-', 3);

  return { tipsAdvice, tipsDiet };
};

const getPetActivityPerDayEnabled = (petInfo: PetInfo): boolean => {
  const mobilityPathosToExclude = PATHOLOGIES.filter(
    (patho) =>
      patho.categories.includes(PathologyCategory.CARDIOVASCULAR_DISEASES) ||
      patho.categories.includes(PathologyCategory.OSTEOARTICULAR_DISORDERS)
  ).map((patho) => patho.value);
  const pathologies = petInfo?.pathologies;
  const sensitivities = petInfo?.sensitivities;

  const petHasMobilityPathos = !!(sensitivities?.length || pathologies?.length)
    ? pathologies.concat(sensitivities).some((patho) => mobilityPathosToExclude.includes(patho))
    : false;
  const petIsOlderThan7 = Helper.getPetAgeObject(petInfo.birthdate).years >= 7;
  return petIsOlderThan7 || petHasMobilityPathos;
};

const consultationActivityPlanPlay = (isPetActivityPerDayEnabled: boolean, poLetterTranslations: Record<string, string>): string => {
  return isPetActivityPerDayEnabled
    ? translateKeyFromCustomLanguage(poLetterTranslations, 'po-letter_consultationActivityPlanPlayEachDay')
    : translateKeyFromCustomLanguage(poLetterTranslations, 'po-letter_consultationActivityPlanPlay');
};

const showPrice = (isRetailPriceActivated: boolean, nutritionData: NutritionData[], isConversionClinic: boolean): boolean => {
  return (
    !isConversionClinic &&
    isRetailPriceActivated &&
    !!(
      nutritionData[0]?.product?.productDetail?.package?.productPrice?.clinicPrice > 0 ||
      nutritionData[1]?.product?.productDetail?.package?.productPrice?.clinicPrice > 0
    )
  );
};

const productsTotalPricePerDay = (
  getTotalPricePerDayValue: number,
  currency: string,
  currencyPipe: CurrencyPipe,
  poLetterTranslations: Record<string, string>
) => {
  return getTotalPricePerDayValue > 0
    ? translateKeyFromCustomLanguage(poLetterTranslations, 'po-letter_cost_day', {
        variableKey: 'cost_value',
        variableValue: currencyPipe.transform(getTotalPricePerDayValue, currency),
      })
    : null;
};

const formatRenalDetectStatus = (status) => {
  return status?.charAt(0).toUpperCase() + status?.substring(1);
};

const formatWeightDifference = (weight1, weight2, unit) => {
  const weightDifference = weight1 && weight2 ? weight1 - weight2 : undefined;
  return `${weightDifference && weightDifference.toFixed(2)} ${unit || VetPreferences.currentBigMeasurementUnit}`;
};

const getPackTypeTranslation = (packType: PackType, quantityPerDay?: number, translations?: Record<string, string>): string => {
  switch (packType) {
    case PackType.SmallCanStr:
      return translateKeyFromCustomLanguage(translations, 'package-type_wet-small-can');

    case PackType.LargeCanStr:
      return translateKeyFromCustomLanguage(translations, 'package-type_wet-large-can');

    case PackType.CanStr:
      return quantityPerDay > 1
        ? translateKeyFromCustomLanguage(translations, 'package-type_wet-cans')
        : translateKeyFromCustomLanguage(translations, 'package-type_wet-can');

    case PackType.PouchStr:
      return translateKeyFromCustomLanguage(translations, 'po-letter_nutriRecoPouch');

    case PackType.BAG:
      return translateKeyFromCustomLanguage(translations, 'package-type_dry-bag');

    case PackType.Kibbles:
      return translateKeyFromCustomLanguage(translations, 'po-letter_nutriRecoKibbles');

    default:
      return '';
  }
};

function getFormattedDailyRation(product, ration, poLetterTranslations) {
  return VetPreferences.currentSystemPreferenceCode === Constants.imperialSystemCode && product.productType === ProductType.Dry
    ? `${
        Math.round(MeasureHelper.convert(ration?.quantityPerDay, MeasureConversionEnum.gramToOunce) * 10) / 10
      } ${MeasureHelper.measureUnitToSuffix(Constants.ounceSuffix)}`
    : `${ration?.quantityPerDay} ${getRationUnit(product, ration, poLetterTranslations)}`;
}

function getDailyRation(product, ration) {
  return VetPreferences.currentSystemPreferenceCode === Constants.imperialSystemCode && product.productType === ProductType.Dry
    ? Math.round(MeasureHelper.convert(ration?.quantityPerDay, MeasureConversionEnum.gramToOunce) * 10) / 10
    : ration?.quantityPerDay;
}

function getDailyRationUnit(product, ration, poLetterTranslations) {
  return VetPreferences.currentSystemPreferenceCode === Constants.imperialSystemCode && product.productType === ProductType.Dry
    ? `${MeasureHelper.measureUnitToSuffix(Constants.ounceSuffix)}`
    : `${getRationUnit(product, ration, poLetterTranslations)}`;
}

const getRationUnit = (product, ration, translations) => {
  return product.productType === ProductType.Wet
    ? getPackTypeTranslation(product?.productDetail?.package?.type, ration?.quantityPerDay, translations)
    : MeasureHelper.measureUnitToSuffix(ration?.unit);
};

const sortPoLetterProducts = (nutritionDatas: NutritionData[]): NutritionData[] => {
  return [...nutritionDatas].sort((product1) => {
    if (product1?.product?.productType === ProductType.Dry) {
      return -1;
    } else {
      return 1;
    }
  });
};

function formatNutritionDataPOLetter(
  nutritionDatas: NutritionData[],
  currency: string,
  currencyPipe: CurrencyPipe,
  fractionCupPipe: FractionCupPipe,
  poLetterTranslations: Record<string, string>
): TemplateProduct[] {
  return sortPoLetterProducts(nutritionDatas).map((nutritionData) => {
    const product = nutritionData.product;
    const ration = nutritionData.basicRation;
    const rationCup = nutritionData.cupRation;
    const dailyRation = {
      quantityPerDay: getDailyRation(product, ration),
      unit: getDailyRationUnit(product, ration, poLetterTranslations),
    };
    return {
      name: product?.productDetail?.details.name,
      productType: product?.productType,
      formattedProductType:
        product?.productType === ProductType.Dry
          ? translateKeyFromCustomLanguage(poLetterTranslations, 'po-letter_nutriRecoKibbles')
          : dailyRation.unit,
      imageLink: `${product?.productDetail?.details?.publicUrl}?w=250&auto=compress`,
      buyLink: '',
      dailyRation,
      formattedDailyRation: getFormattedDailyRation(product, ration, poLetterTranslations),
      formattedRationbyCup:
        product.productType === ProductType.Dry
          ? `(${fractionCupPipe.transform(rationCup?.quantityPerDay)} ${translateKeyFromCustomLanguage(
              poLetterTranslations,
              'po-letter_nutriRecoCup'
            )})`
          : '',
      formattedPackSize: product?.productDetail?.package?.text, // '85g'
      formattedRationbyDay: translateKeyFromCustomLanguage(poLetterTranslations, 'po-letter_day_bag', {
        variableKey: 'number_of_days',
        variableValue: '≈ ' + ration?.nbDaysPerPack,
      }), // '(≈ 53 day(s) of food)'
      formattedPrice:
        product?.productDetail?.package?.productPrice?.clinicPrice && product?.productDetail?.package?.productPrice?.clinicPrice > 0
          ? currencyPipe.transform(product?.productDetail?.package?.productPrice?.clinicPrice, currency)
          : '--', // '24,99$'
      formattedCalories: `${Math.floor(ration?.kcalPerDay)} ${translateKeyFromCustomLanguage(
        poLetterTranslations,
        'Kcal'
      )}/${translateKeyFromCustomLanguage(poLetterTranslations, 'day')}`, // '423kCal/g'
    };
  });
}

export function getPOLetterTemplateName({
  tool,
  petInfo,
  currentConsultation,
  poLetterFrom,
}: {
  tool: Tool;
  petInfo: PetInfo;
  currentConsultation: Consultation;
  poLetterFrom: POLetterFrom;
}): PoLetterType {
  if (poLetterFrom === POLetterFrom.FlowAllowanceWeightBlock) {
    return PoLetterType.WeightLoss;
  } else {
    switch (tool) {
      case Tool.Rationing:
      case Tool.PersonalizedBag:
        return PoLetterType.DailyAllowance;
      case Tool.SmartReco:
        return PoLetterType.SmartReco;
      case Tool.RenalDetect:
        if (
          petInfo?.pathologies?.length > 0 ||
          currentConsultation?.pathologies?.length > 0 ||
          petInfo?.sensitivities?.length > 0 ||
          currentConsultation?.sensitivities?.length > 0
        ) {
          return PoLetterType.RenalSmartReco;
        } else {
          return PoLetterType.DailyRenalDetect;
        }
      case Tool.WeightManagement:
        return poLetterFrom === POLetterFrom.WeightTab ? PoLetterType.WeightLoss : PoLetterType.DailyAllowance;
      default:
        return PoLetterType.DailyAllowance;
    }
  }
}

export function getObservationMaxLength(
  tool: Tool,
  additionalInformation: { petInfo?: PetInfo; consultation?: Consultation; renalDetectNutritionalChoice?: NutritionalChoice } | null = null
): string {
  const petInfo = additionalInformation?.petInfo;
  const consultation = additionalInformation?.consultation;
  const renalDetectNutritionalChoice = additionalInformation?.renalDetectNutritionalChoice;
  const hasPathologies =
    petInfo?.pathologies?.length > 0 ||
    consultation?.pathologies?.length > 0 ||
    petInfo?.sensitivities?.length > 0 ||
    consultation?.sensitivities?.length > 0;

  switch (tool) {
    case Tool.SmartReco:
      if (!additionalInformation || (additionalInformation && hasPathologies)) {
        return '215';
      }
      return '415';
    case Tool.WeightManagement:
      return '215';
    case Tool.RenalDetect:
      if (
        !additionalInformation ||
        (additionalInformation && hasPathologies) ||
        renalDetectNutritionalChoice === NutritionalChoice.SmartReco
      ) {
        return '120';
      }
      return '204';
    default:
      return '415';
  }
}

export function formatWeightsToChart(consultationWeights: ConsultationWeight[], formattedIbw: number): WeightChart[] {
  const lastWeights = getLastWeights(consultationWeights);
  const weightSvg = getWeightSvgSize(lastWeights, formattedIbw);
  return lastWeights.map(({ measure, measureUnit, weightDate }) => {
    // {measure:3,measureUnit:"kg",weightDate:"2022-11-02T23:00:00.000Z"},svgSize:{mini: true}
    return { measure: +measure.toFixed(2), measureUnit, weightDate, svgSize: weightSvg[measure] };
  });
}

function getWeightSvgSize(weights: Weight[], formattedIbw: number): Record<number, { [key in SvgSize]?: boolean }> {
  const weightAndSvg: Record<number, { [key in SvgSize]?: boolean }> = {};

  //case 1: measureWeights: [3,5,4] & formattedIbw:4
  //case 2: measureWeights: [7,9,8] & formattedIbw:4
  //case 3: measureWeights: [6,5,5] & formattedIbw:4
  const measureWeights = weights.map(({ measure }) => measure);
  //case 1: isAllWeightsGreaterIbw =false
  //case 2: isAllWeightsGreaterIbw =true
  //case 3: isAllWeightsGreaterIbw =true
  const isAllWeightsGreaterIbw = measureWeights.every((el) => el > formattedIbw);
  const svgGtIbw = [SvgSize.S, SvgSize.M, SvgSize.L];

  //case 1: weightWithoutOccurenceSorted=[3,4,5]
  //case 2: weightWithoutOccurenceSorted=[9,8,7]
  //case 2: weightWithoutOccurenceSorted=[6,5]
  const weightWithoutOccurenceSorted = [...new Set(measureWeights)].sort((a, b) =>
    isAllWeightsGreaterIbw ? Helper.compareFn(b, a) : Helper.compareFn(a, b)
  );

  weightWithoutOccurenceSorted.forEach((measure) => {
    if (measure < formattedIbw) {
      // case 1: measure=3    '3': { MINI: true }

      weightAndSvg[measure] = { [SvgSize.MINI]: true };
    } else if (measure === formattedIbw) {
      // case 1: measure=4    '4': { EQUAL: true }

      weightAndSvg[measure] = { [SvgSize.EQUAL]: true };
    } else {
      const selectedSvg = isAllWeightsGreaterIbw ? svgGtIbw.pop() : svgGtIbw.shift();
      weightAndSvg[measure] = { [selectedSvg]: true };
      // case 1: measure=5; selectedSvg= S; '4': { EQUAL: true }

      // case 2: measure=9; selectedSvg= L; '9': { L: true }
      // case 2: measure=8; selectedSvg= M; '8': { M: true }
      // case 2: measure=7; selectedSvg= S; '7': { S: true }

      // case 3: measure=6; selectedSvg= L; '4': { L: true }
      // case 3: measure=5; selectedSvg= L; '4': { M: true }
    }
  });
  // case 1: weightAndSvg= { '3': { MINI: true }, '4': { EQUAL: true }, '5': { S: true } }
  // case 2 weightAndSvg = { '7': { S: true }, '8': { M: true }, '9': { L: true } }
  // case 3 weightAndSvg ={ '5': { M: true }, '6': { L: true } }
  return weightAndSvg;
}

function getLastWeights(consultationWeights: ConsultationWeight[]): Weight[] {
  return consultationWeights
    .map(({ weight }) => {
      const formatteWeight = MeasureHelper.convertWeight(
        weight.measure,
        weight.measureUnit,
        VetPreferences.currentBigMeasurementUnit,
        VetPreferences.currentSystemPreferenceCode
      );
      return { ...weight, measure: formatteWeight.measure, measureUnit: formatteWeight.measureUnit };
    })
    .sort(({ weightDate: a }, { weightDate: b }) => Helper.compareFn(a, b)) // sort by OLD Date
    .slice(-3);
}

function ellipsis(myString: string, maxLength: number, toKeepLength: number): string {
  return myString.length > maxLength ? `${myString.substring(0, toKeepLength)} ...` : myString;
}

function filteredCriteriasByPmean(recommendationsCriterias: Criteria[], pmeanMin: number): Criteria[] {
  return getPathologicalCriterias(recommendationsCriterias)?.filter((criteria: Criteria) => roundSmartReco(criteria.pmean) >= pmeanMin);
}

export function getTipsOfCriterias(
  poLetterTranslationsData: Record<string, string>,
  criterias: Criteria[],
  petSpecie: SpeciesCode,
  prefixKeyTrad: string,
  numberMaxOfCriteria: number
): { title: string; tips: string[] }[] {
  if (!criterias) return [];

  const result = [];
  const allAdviceTranslatedCriteria = []; // save each key for prevent occurrences
  const tipsAtHomeKeys = getTranslationKeyStartWith(Object.keys(poLetterTranslationsData), prefixKeyTrad);

  criterias.forEach((criteria) => {
    if (result.length === numberMaxOfCriteria) return;
    const currentAdviceCriteriaKeys = getAdviceFromCritera(criteria, tipsAtHomeKeys);
    const currentAdviceCriteriaKeysFilteredBySpecie = getFilteredKeysBySpecie(petSpecie, currentAdviceCriteriaKeys);

    const currentAdviceCriteriaTranslated = currentAdviceCriteriaKeysFilteredBySpecie
      .map((key) => translateKeyFromCustomLanguage(poLetterTranslationsData, key))
      .filter((translatedKey) => {
        if (allAdviceTranslatedCriteria.includes(translatedKey)) return false; // advice already pushed
        allAdviceTranslatedCriteria.push(translatedKey);
        return true;
      });

    if (currentAdviceCriteriaTranslated.length) {
      result.push({
        title: translateKeyFromCustomLanguage(
          poLetterTranslationsData,
          `${criteria.type === CriteriaType.Diseases ? 'pathology' : 'sensitivity'}-${criteria.key}`
        ),
        tips: currentAdviceCriteriaTranslated,
      });
    }
  });

  return result;
}

export function getPathologicalCriterias(criterias: Criteria[]): Criteria[] {
  return (
    criterias?.filter((criteria: Criteria) => [CriteriaType.Diseases, CriteriaType.RiskFactor].includes(criteria.type as CriteriaType)) ||
    null
  );
}

export function getFilteredKeysBySpecie(petSpecie: SpeciesCode, keys: string[]): string[] {
  return keys.filter((key) => {
    const specieOccurence = key.split('-').find((item) => ([SpeciesCode.Cat, SpeciesCode.Dog] as string[]).includes(item));
    return !specieOccurence || specieOccurence === petSpecie;
  });
}

export function getAdviceFromCritera(criteria: Criteria, keys: string[]): string[] {
  return keys.filter((key) => key.split('-').includes(criteria.key));
}
